import {
  type CylinderPlacementIssue,
  type CylinderSurface,
  cylinderPlacementIssue,
  GEOMETRY_TOLERANCE,
  isPlacementInsideOutline,
  isPlacementInsideSurface,
  type PlacementPosition,
  type ShapedComponent,
  type SizeMm,
  type Surface,
} from '@repo/api-contracts';
import { surfacePhotoToMm, surfaceRectOnPhoto, surfaceScale } from '@repo/design-renderer';

/**
 * 표면 타입으로 분기하는 mm↔사진 변환·배치 규칙의 진입점. 오버레이·배치 포인터·Grid·배치 명령이 이 파일을 호출한다.
 * - flat: 축 정렬 사각형이라 축척 하나로 옮긴다(flat-surface-mapping).
 * - cylinder: 원통 변환으로 옮긴다(cylinder-surface-mapping, 엔진 frameToWarped·warpedToFrame). 가로 축척이 위치마다
 *   달라 축척은 가운데 열 값이다. 배치 경계가 곡선이라 사각형(surfaceRectOnPhoto)은 주지 않는다 — 곡선 경계와
 *   격자선은 cylinder-surface-mapping 의 cylinderRectOnPhoto·cylinderGridOnPhoto 가 준다.
 * - mesh: 이 화면은 조회·버전 탐색만 하므로(ADR-0007 D10) 옮기지 않고 null 을 돌려준다. 호출자는 그리지 않는다.
 */
export { surfacePhotoToMm, surfaceRectOnPhoto, surfaceScale };

/**
 * 배치를 이 표면에 둘 수 있는가. 저장 계약과 같은 함수를 쓴다.
 * - cylinder: 원통 배치 규칙(cylinderPlacementIssue, ADR-0007 D9). 세로는 프레임 안에 모두 들어가야 하고, 가로는
 *   프레임과 겹치기만 하면 되므로 음수 위치와 프레임보다 넓은 Component(둘레 2W 까지)도 받는다.
 * - flat·mesh: 표면 실측 범위 안이고, 윤곽이 있는 flat 표면이면 윤곽 안이다.
 */
export const isPlacementAllowed = (
  component: ShapedComponent,
  surface: Surface,
  position: PlacementPosition,
): boolean => {
  if (surface.type === 'cylinder')
    return cylinderPlacementIssue(component, surface.realSize, position) === null;
  if (!isPlacementInsideSurface(component, surface.realSize, position)) return false;
  if (surface.type !== 'flat' || surface.outline === undefined) return true;

  return isPlacementInsideOutline(
    component,
    { realSize: surface.realSize, points: surface.points, outline: surface.outline },
    position,
  );
};

const INITIAL_POSITION_GRID_STEPS = 20;

/** 소수 둘째 자리로 내림 — 내림이라 후보가 실측 범위를 넘지 않는다. */
const floorToHundredths = (value: number): number => Math.floor(value * 100) / 100;

/**
 * 원통 표면의 가운데 위치 ((W − w)/2, (H − h)/2). 윤곽 제약이 없어 새 배치는 가운데 하나만 본다.
 * 프레임보다 넓은 Component 는 x 가 음수다(ADR-0007 D9). 높이가 허용오차 안에서 프레임보다 크면 (H − h)/2 가
 * 0 아래로 내려가는데, 배치 위치 계약은 yMm ≥ 0 이라 0 으로 자른다.
 */
const cylinderCenterOf = (component: SizeMm, surface: CylinderSurface): PlacementPosition => ({
  xMm: (surface.realSize.widthMm - component.widthMm) / 2,
  yMm: Math.max(0, (surface.realSize.heightMm - component.heightMm) / 2),
});

/**
 * 원통 표면 가운데에 새 배치를 둘 수 없는 사유(계약 cylinderPlacementIssue). 둘 수 있으면 null 이다.
 * 둘레 2W 보다 넓으면 WIDER_THAN_CIRCUMFERENCE, 프레임보다 높으면 OUTSIDE_FRAME_HEIGHT — 화면의 '자리 없음' 안내가
 * findInitialPlacementPosition 이 null 을 돌려준 까닭으로 보인다.
 */
export const initialCylinderPlacementIssue = (
  component: SizeMm,
  surface: CylinderSurface,
): CylinderPlacementIssue | null =>
  cylinderPlacementIssue(component, surface.realSize, cylinderCenterOf(component, surface));

/** 원통 표면에 새 배치를 처음 놓을 위치 — 가운데. 둘레 2W 보다 넓거나 프레임보다 높으면 null. */
const centeredCylinderPosition = (
  component: SizeMm,
  surface: CylinderSurface,
): PlacementPosition | null =>
  initialCylinderPlacementIssue(component, surface) === null
    ? cylinderCenterOf(component, surface)
    : null;

export type PlacementAxis = 'x' | 'y';

/**
 * 배치를 표면 가운데로 옮긴 위치 — 한 축만 바꾸고 다른 축은 그대로 둔다(배치 인스펙터의 [가로 가운데]·[세로 가운데]).
 * 가운데 기준은 표면 실측 프레임이라 원통(프레임 π×지름/2)에서도 맞는다. 원통은 프레임보다 넓은 Component 의
 * x 가 음수일 수 있고(ADR-0007 D9), 그 밖의 표면은 배치 위치 계약의 하한대로 0 아래로 내려가지 않는다.
 * 윤곽 안인지는 여기서 보지 않는다 — 적용 시 스토어 명령이 저장 계약과 같은 규칙으로 판정한다.
 */
export const centerPlacementOnAxis = (
  component: SizeMm,
  surface: Surface,
  current: PlacementPosition,
  axis: PlacementAxis,
): PlacementPosition => {
  const center =
    surface.type === 'cylinder'
      ? cylinderCenterOf(component, surface)
      : {
          xMm: Math.max(0, (surface.realSize.widthMm - component.widthMm) / 2),
          yMm: Math.max(0, (surface.realSize.heightMm - component.heightMm) / 2),
        };
  return axis === 'x' ? { ...current, xMm: center.xMm } : { ...current, yMm: center.yMm };
};

/**
 * 새 배치를 처음 놓을 위치. 원통 표면은 가운데에 둔다(centeredCylinderPosition).
 * 그 밖의 표면은 (0, 0) → 가운데 → 위에서부터 훑는 격자 순으로 처음 허용되는 자리를 고른다.
 * 윤곽이 외접 사각형의 왼쪽 위를 채우지 않는 제품(컵·사다리꼴)에서는 (0, 0) 이 윤곽 밖이라 배치를 만들 수 없기 때문이다.
 * Component 가 표면 실측보다 크거나 들어갈 자리가 없으면 null.
 */
export const findInitialPlacementPosition = (
  component: SizeMm,
  surface: Surface,
): PlacementPosition | null => {
  if (surface.type === 'cylinder') return centeredCylinderPosition(component, surface);
  const roomX = surface.realSize.widthMm - component.widthMm;
  const roomY = surface.realSize.heightMm - component.heightMm;
  if (roomX < -GEOMETRY_TOLERANCE || roomY < -GEOMETRY_TOLERANCE) return null;
  const spanX = Math.max(0, roomX);
  const spanY = Math.max(0, roomY);
  const steps = Array.from({ length: INITIAL_POSITION_GRID_STEPS + 1 }, (_, step) => step);
  const candidates: PlacementPosition[] = [
    { xMm: 0, yMm: 0 },
    { xMm: spanX / 2, yMm: spanY / 2 },
    ...steps.flatMap((yStep) =>
      steps.map((xStep) => ({
        xMm: floorToHundredths((spanX * xStep) / INITIAL_POSITION_GRID_STEPS),
        yMm: floorToHundredths((spanY * yStep) / INITIAL_POSITION_GRID_STEPS),
      })),
    ),
  ];

  return candidates.find((position) => isPlacementAllowed(component, surface, position)) ?? null;
};
